import { useMemo, useState } from 'react'

import { useCompanies, useFields } from '../api/hooks'
import { подсказкаПустойДаты } from '../api/istochnik'
import type { ListParams } from '../api/types'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtText } from '../lib/format'

const PAGE = 50

export default function Companies() {
  const [title, setTitle] = useState('')
  const [start, setStart] = useState(0)

  const params = useMemo<ListParams>(() => {
    const filter: Record<string, unknown> = {}
    if (title.trim()) filter['%TITLE'] = title.trim()
    return { filter, order: { DATE_MODIFY: 'DESC' }, start }
  }, [title, start])

  const q = useCompanies(params)
  const data = q.data
  // названия колонок и значения справочных полей берём из ядра, а не зашиваем здесь
  const f = useFields('company')

  // Подписи колонок считаются один раз и идут и в шапку, и в data-label ячейки: на телефоне
  // таблица разбирается на карточки, шапки там нет, а две копии подписи разъехались бы при
  // первом переименовании поля в Bitrix. Тот же приём, что в PaymentsTable.
  const З = {
    id: 'ID',
    title: 'Название',
    type: f.title('COMPANY_TYPE'),
    industry: f.title('INDUSTRY'),
    modified: 'Изменена',
  }

  return (
    <>
      <div className="page-head">
        <h1>Компании</h1>
        {/* «в вашей зоне» рядом с числом, а не только в оговорке ниже: счётчик читают
            отдельно от текста, и «найдено: 1» без этой приписки — часть выдана за целое. */}
        <span className="muted">
          {data && !data.stale
            ? `найдено: ${data.total}${data.scope?.limited ? ' (в вашей зоне)' : ''}`
            : ''}
        </span>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="companies">
          <div className="f" style={{ flex: 1, minWidth: 240 }}>
            <label htmlFor="ct">Название содержит</label>
            <input id="ct" value={title} onChange={(e) => { setStart(0); setTitle(e.target.value) }} />
          </div>
        </FilterBar>
      </div>

      <QueryState isLoading={q.isLoading} error={q.error} stale={data?.stale} ageMin={data?.ageMin}
                  scope={data?.scope}>
        <div className="card">
          {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th className="nowrap">{З.id}</th>
                  <th>{З.title}</th>
                  <th className="nowrap">{З.type}</th>
                  <th className="nowrap">{З.industry}</th>
                  <th className="nowrap">{З.modified}</th>
                </tr>
              </thead>
              <tbody>
                {(data?.items ?? []).map((c) => (
                  <tr key={c.ID}>
                    <td className="mono nowrap" data-label={З.id}><EntityLink kind="company" id={c.ID} title={c.ID} /></td>
                    <td data-label={З.title}><EntityLink kind="company" id={c.ID} title={fmtText(c.TITLE)} /></td>
                    {/* название поля и значение — из справочника ядра, а не зашиты здесь */}
                    <td className="nowrap muted" data-label={З.type}>{fmtText(f.enumText('COMPANY_TYPE', c.COMPANY_TYPE))}</td>
                    <td className="nowrap muted" data-label={З.industry}>{fmtText(f.enumText('INDUSTRY', c.INDUSTRY))}</td>
                    {/* В своей базе времени правки Bitrix нет: прочерк с подсказкой. */}
                    <td className="nowrap muted" data-label={З.modified}
                        title={подсказкаПустойДаты(c.DATE_MODIFY)}>{fmtDate(c.DATE_MODIFY, true)}</td>
                  </tr>
                ))}
                {data && !data.stale && data.items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="empty">
                      {/* «в вашей зоне таких компаний нет» и «таких компаний нет» — разные
                          утверждения: во втором человек ищет дальше, в первом — идёт к тому,
                          у кого зона шире. */}
                      <div className="empty-title">
                        {data.scope?.limited
                          ? 'В вашей зоне таких компаний нет — выборка сужена правами, а не фильтрами'
                          : 'Ничего не найдено под эти фильтры'}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {data && data.total > PAGE && (
            <div className="pager">
              <button disabled={start === 0 || q.isFetching} onClick={() => setStart(Math.max(0, start - PAGE))}>← назад</button>
              <span className="muted">{start + 1}–{Math.min(start + PAGE, data.total)} из {data.total}</span>
              <button disabled={data.next === undefined || q.isFetching} onClick={() => setStart(data.next!)}>вперёд →</button>
            </div>
          )}
        </div>
      </QueryState>
    </>
  )
}
